import { useState } from 'react';
import { Printer, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getStudentQuotasSummary, generatePrintPdf } from '../../../api/drmQuotasService';
import type { BookFormat } from '../types';
import styles from '../ReaderPanels.module.css';
import { useAlert } from '../../../contexts/AlertContext';
import { useTranslation } from '../../../i18n/useTranslation';
import { controlClasses } from './controls';

interface PrintPanelProps {
  bookId: string;
  totalPages: number;
  format: BookFormat;
}

export default function PrintPanel({ bookId, totalPages = 0, format }: PrintPanelProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useAlert();
  const studentId = user?.sub || '';

  const [startPage, setStartPage] = useState<string>('1');
  const [endPage, setEndPage] = useState<string>('1');
  const [isPrinting, setIsPrinting] = useState(false);

  // Fetch DRM quotas
  const {
    data: quotas,
    isLoading: isLoadingQuotas,
    error: quotasError,
  } = useQuery({
    queryKey: ['drm-quotas', studentId, bookId],
    queryFn: () => getStudentQuotasSummary(studentId, bookId),
    enabled: !!studentId && !!bookId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const handlePrint = async () => {
    if (!quotas) return;

    const start = parseInt(startPage, 10);
    const end = parseInt(endPage, 10);

    // Validation
    if (isNaN(start) || isNaN(end)) {
      showToast(t('App.Reader.Print.ValidationInvalidPages'), { type: 'warning' });
      return;
    }

    if (start < 1 || end < 1) {
      showToast(t('App.Reader.Print.ValidationMinPage'), { type: 'warning' });
      return;
    }

    if (start > totalPages || end > totalPages) {
      showToast(t('App.Reader.Print.ValidationMaxPage', { totalPages }), { type: 'warning' });
      return;
    }

    if (start > end) {
      showToast(t('App.Reader.Print.ValidationStartEnd'), { type: 'warning' });
      return;
    }

    const pageCount = end - start + 1;

    if (pageCount > quotas.print.remaining) {
      showToast(
        t('App.Reader.Print.QuotaExceededMessage', { pageCount, remaining: quotas.print.remaining }),
        { type: 'warning', title: t('App.Reader.Print.QuotaExceededTitle') }
      );
      return;
    }

    setIsPrinting(true);
    try {
      // Generate watermarked PDF via backend API
      const result = await generatePrintPdf({
        studentId,
        bookId,
        startPage: start,
        endPage: end,
      });

      if (!result.isApproved) {
        showToast(result.message, { type: 'error' });
        return;
      }

      if (!result.downloadUrl) {
        showToast(t('App.Reader.Print.GenerateFailed'), { type: 'error' });
        return;
      }

      // Open watermarked PDF in new window and trigger print dialog
      const printWindow = window.open(result.downloadUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback if popup blocked
        window.location.href = result.downloadUrl;
      }

      // Optimistically update the cache with the authoritative remainingQuota
      // from the API response so the UI reflects the new value immediately.
      queryClient.setQueryData(
        ['drm-quotas', studentId, bookId],
        (old: typeof quotas | undefined) =>
          old
            ? { ...old, print: { ...old.print, remaining: result.remainingQuota } }
            : old
      );
      // Still invalidate so other quotas (copy/download) stay in sync with backend.
      queryClient.invalidateQueries({ queryKey: ['drm-quotas', studentId, bookId] });

      showToast(t('App.Reader.Print.Success', { remainingQuota: result.remainingQuota }), {
        type: 'success',
      });
    } catch (error) {
      console.error('Print failed:', error);
      showToast(t('App.Reader.Print.Error'), { type: 'error' });
    } finally {
      setIsPrinting(false);
    }
  };

  if (!user) {
    return (
      <div className={styles.panel}>
        <div className="flex items-center gap-2 mb-4">
          <Printer className="w-5 h-5 text-neutral-700" />
          <h2 className="text-lg font-semibold text-neutral-900">{t('App.Reader.Print.Title')}</h2>
        </div>
        <p className="text-sm text-neutral-500">{t('App.Reader.Print.LoginRequired')}</p>
      </div>
    );
  }

  if (isLoadingQuotas) {
    return (
      <div className={styles.panel}>
        <div className="flex items-center gap-2 mb-4">
          <Printer className="w-5 h-5 text-neutral-700" />
          <h2 className="text-lg font-semibold text-neutral-900">{t('App.Reader.Print.Title')}</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t('App.Reader.Print.LoadingQuota')}</span>
        </div>
      </div>
    );
  }

  if (quotasError || !quotas) {
    return (
      <div className={styles.panel}>
        <div className="flex items-center gap-2 mb-4">
          <Printer className="w-5 h-5 text-neutral-700" />
          <h2 className="text-lg font-semibold text-neutral-900">{t('App.Reader.Print.Title')}</h2>
        </div>
        <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-error-700">
            {t('App.Reader.Print.QuotaLoadError')}
          </p>
        </div>
      </div>
    );
  }

  const printRemaining = quotas.print.remaining;
  const isPrintExhausted = printRemaining <= 0;

  return (
    <div className={styles.panel} style={{ width: '100%', padding: '16px' }}>
      {/* Quota Display */}
      <div
        className="mb-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-md text-xs"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="font-semibold text-neutral-700">{t('App.Reader.Print.Remaining')}:</span>{' '}
        <span
          className={`font-bold ${
            printRemaining > 10
              ? 'text-success-600'
              : printRemaining > 0
                ? 'text-warning-600'
                : 'text-error-600'
          }`}
        >
          {t('App.Reader.Print.RemainingPages', { count: printRemaining })}
        </span>
      </div>

      {isPrintExhausted ? (
        <div className="flex items-start gap-2 p-2 bg-warning-50 border border-warning-200 rounded-md">
          <AlertCircle className="w-4 h-4 text-warning-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-warning-700">
            <p className="font-semibold mb-0.5">{t('App.Reader.Print.QuotaExhaustedTitle')}</p>
            <p>{t('App.Reader.Print.QuotaExhaustedMessage')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Page Range Selection */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div>
              <label htmlFor="startPage" className={controlClasses.fieldLabel}>
                {t('App.Reader.Print.StartPage')}
              </label>
              <input
                id="startPage"
                type="number"
                min={1}
                max={totalPages}
                value={startPage}
                onChange={(e) => setStartPage(e.target.value)}
                className={controlClasses.input}
                placeholder="1"
              />
            </div>
            <div>
              <label htmlFor="endPage" className={controlClasses.fieldLabel}>
                {t('App.Reader.Print.EndPage')}
              </label>
              <input
                id="endPage"
                type="number"
                min={1}
                max={totalPages}
                value={endPage}
                onChange={(e) => setEndPage(e.target.value)}
                className={controlClasses.input}
                placeholder="1"
              />
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-neutral-500 mb-3">
            <span>{t('App.Reader.Print.TotalPages', { totalPages })}</span>
            <span>
              {t('App.Reader.Print.ToPrint')}{' '}
              <span className="font-semibold text-neutral-700">
                {(() => {
                  const start = parseInt(startPage, 10);
                  const end = parseInt(endPage, 10);
                  if (isNaN(start) || isNaN(end) || start > end) return '-';
                  return end - start + 1;
                })()}
              </span>
            </span>
          </div>

          {/* Print Button */}
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className={`${controlClasses.btnPrimary} w-full flex items-center justify-center gap-1.5`}
          >
            {isPrinting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('App.Reader.Print.Preparing')}</span>
              </>
            ) : (
              <>
                <Printer className="w-4 h-4" />
                <span>{t('App.Reader.Print.PrintButton')}</span>
              </>
            )}
          </button>

          {/* Info */}
          <p className="mt-2 text-[11px] text-neutral-500 leading-snug">
            {t('App.Reader.Print.Note')}
          </p>
        </>
      )}
    </div>
  );
}
